<script setup>
import { computed } from 'vue'
import AppBadge from '@/components/atoms/AppBadge'
import AppCard from '@/components/molecules/AppCard'
import HelpHubStepMarker from './HelpHubStepMarker.vue'

const props = defineProps({
  as: { type: String, default: 'li' },
  title: { type: String, required: true },
  description: { type: String, default: '' },
  // Null on a row without a marker: a search result, or a conversation or a ticket on the Chat tab.
  icon: { type: String, default: 'heroicons-outline:link' },
  // None when the card itself is the control: `as="a"` with an `href`, or `as="button"` with a click, falling through.
  to: { type: [String, Object], default: null },
  // Lines the title may run to. The topic rows are one line of a known label; a popular
  // question is the dealer's own phrasing and Figma gives it two.
  titleLines: { type: Number, default: 1 },
  // A label over the title, in an AppBadge variant: a search result's source.
  badge: { type: String, default: '' },
  badgeVariant: { type: String, default: 'gray' },
})

// Spelled out, not interpolated: Tailwind scans source text, so a built class name like
// `line-clamp-${n}` is never generated and the title would silently stop clamping.
const TITLE_LINES = { 1: 'truncate', 2: 'line-clamp-2', 3: 'line-clamp-3' }

const titleClass = computed(() => TITLE_LINES[props.titleLines] ?? TITLE_LINES[1])
</script>

<template>
  <AppCard :as="as" size="sm" :to="to" :title="title" hoverable>
    <template v-if="icon" #leading>
      <!-- Figma's card centres the marker on the text: one line, two, or a title with its description. -->
      <span class="flex h-full items-center">
        <HelpHubStepMarker size="md" variant="filled">
          <!-- Figma draws this 20px icon with a 2px stroke. -->
          <Icon :icon="icon" class="size-5 [&>path]:stroke-[2.4px]" />
        </HelpHubStepMarker>
      </span>
    </template>

    <template #title>
      <!-- Under a badge the title is a column, 4px apart as Figma stacks them; max-w-full keeps it truncating there. -->
      <div v-if="badge" class="flex min-w-0 flex-1 flex-col items-start gap-y-1">
        <AppBadge size="xs" :variant="badgeVariant">{{ badge }}</AppBadge>
        <span class="max-w-full font-medium" :class="titleClass" v-text="title" />
      </div>
      <span v-else class="min-w-0 font-medium" :class="titleClass" v-text="title" />
    </template>

    <!-- Only with text: an empty one would still open the card's 8px gap under the title. -->
    <template v-if="description" #description>
      <p class="truncate" v-text="description" />
    </template>

    <template #trailing>
      <!-- A ticket's badges take the chevron's place. -->
      <slot name="trailing">
        <Icon icon="heroicons-solid:chevron-right" class="size-5 text-gray-300" />
      </slot>
    </template>
  </AppCard>
</template>
